/**
 * Tooltip: a small inverse-coloured label shown on hover or keyboard focus.
 *
 * The child stays the single interactive element; the tooltip is portalled to
 * `document.body` (the rail lives inside nested scrollers and a transformed
 * modal, so an in-flow tooltip would clip) and positioned `fixed` from the
 * trigger's rect with the shared `resolvePopoverPosition` clamp.
 *
 * Behaviour that is easy to regress:
 * - Escape is handled on the CAPTURE phase with both stops, and only while
 *   open (a hover-opened tip with focus on another control closes but lets
 *   the key pass). The card `<Modal>` registered its bubble listener first, so a
 *   bubble-phase handler here would run after the Modal had already read
 *   `defaultPrevented === false` and one keystroke would close the tooltip
 *   AND the card. A second Escape (tooltip gone) reaches the Modal.
 * - Focus opens only on `:focus-visible`, so a mouse click on the trigger
 *   never pops a tooltip.
 * - `lastClosedAt` is module-level: sweeping across a stack of avatars should
 *   read as one tooltip that moves, so an open within the warm window skips
 *   the hover delay.
 * - The box is first rendered hidden at 0,0 so `w-max` measures the
 *   uncompressed width; a `fixed` box with only `left` set would otherwise
 *   shrink to fit the space left of the viewport edge.
 */

import {
  type FocusEvent,
  type ReactElement,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { resolvePopoverPosition } from '@/lib/anchor-popover'

const WARM_WINDOW_MS = 400
const EDGE_MARGIN = 8
const GAP = 6

let lastClosedAt = 0

/** Test seam: forget the warm hand-off between cases. */
export function resetTooltipWarmState(): void {
  lastClosedAt = 0
}

export interface TooltipProps {
  content: ReactNode
  placement?: 'top' | 'bottom'
  openDelay?: number
  closeDelay?: number
  disabled?: boolean
  /** Wire `aria-describedby` while open. Pass false for aria-hidden decorative triggers. */
  describe?: boolean
  /** Which interactions open it. `focus` is for a control whose hover content is elsewhere. */
  triggers?: 'both' | 'hover' | 'focus'
  /** One element, cloned with the trigger handlers. */
  children: ReactElement
}

type Handler<E> = ((event: E) => void) | undefined

function isFocusVisible(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  try {
    return target.matches(':focus-visible')
  } catch {
    // Engines without the selector: err toward showing it to keyboard users.
    return true
  }
}

export function Tooltip({
  content,
  placement = 'top',
  openDelay = 300,
  closeDelay = 0,
  disabled = false,
  describe = true,
  triggers = 'both',
  children,
}: TooltipProps) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const anchorRef = useRef<Element | null>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const openRef = useRef(false)
  // How the open tooltip was triggered; decides whether Escape is ours.
  const openedByRef = useRef<'hover' | 'focus'>('hover')
  useEffect(() => {
    openRef.current = open
  }, [open])

  const hide = useCallback(() => {
    clearTimer()
    if (openRef.current) lastClosedAt = Date.now()
    setOpen(false)
    setPos(null)
  }, [clearTimer])

  const show = useCallback(
    (delay: number) => {
      clearTimer()
      const warm = Date.now() - lastClosedAt < WARM_WINDOW_MS
      const wait = warm ? 0 : delay
      if (wait <= 0) {
        openedByRef.current = 'hover'
        setOpen(true)
        return
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        openedByRef.current = 'hover'
        setOpen(true)
      }, wait)
    },
    [clearTimer],
  )

  const scheduleHide = useCallback(() => {
    if (closeDelay <= 0) {
      hide()
      return
    }
    clearTimer()
    timerRef.current = setTimeout(hide, closeDelay)
  }, [closeDelay, hide, clearTimer])

  useEffect(() => clearTimer, [clearTimer])

  // Close when disabled flips on while open.
  useEffect(() => {
    if (disabled) hide()
  }, [disabled, hide])

  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const tip = tipRef.current
      const anchor = anchorRef.current
      if (!tip || !anchor) return
      const rect = anchor.getBoundingClientRect()
      const size = { width: tip.offsetWidth, height: tip.offsetHeight }
      const { top } = resolvePopoverPosition({
        anchor: rect,
        size,
        prefer: placement === 'top' ? 'above' : 'below',
        margin: GAP,
        avoidAnchorOverlap: false,
      })
      const centred = rect.left + rect.width / 2 - size.width / 2
      const left = Math.max(
        EDGE_MARGIN,
        Math.min(window.innerWidth - EDGE_MARGIN - size.width, centred),
      )
      setPos({ top, left })
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [open, placement])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // A hover-opened tooltip only owns Escape while focus is on (or in) its
      // trigger, on a dialog box itself, or nowhere; with focus on another
      // control (a field being edited while the pointer rests on an avatar)
      // the key belongs to that control. A focused dialog box has no Escape
      // of its own besides closing, so the tooltip takes the first press.
      const active = document.activeElement
      const anchor = anchorRef.current
      const ours =
        openedByRef.current === 'focus' ||
        !active ||
        active === document.body ||
        active.getAttribute('role') === 'dialog' ||
        anchor?.contains(active)
      hide()
      if (!ours) return
      e.preventDefault()
      e.stopPropagation()
    }
    // Capture: a scroll inside any ancestor moves the anchor out from under us.
    const onScroll = () => hide()
    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('scroll', onScroll, true)
    }
  }, [open, hide])

  if (!isValidElement(children)) return children

  const child = children as ReactElement<Record<string, unknown>>
  const own = child.props as {
    onPointerEnter?: Handler<ReactPointerEvent>
    onPointerLeave?: Handler<ReactPointerEvent>
    onPointerDown?: Handler<ReactPointerEvent>
    onFocus?: Handler<FocusEvent>
    onBlur?: Handler<FocusEvent>
    onKeyDown?: Handler<React.KeyboardEvent>
    'aria-describedby'?: string
  }
  const hover = triggers !== 'focus'
  const focus = triggers !== 'hover'

  const merged: Record<string, unknown> = {
    onPointerEnter: (e: ReactPointerEvent) => {
      own.onPointerEnter?.(e)
      if (!hover || disabled || e.pointerType === 'touch') return
      anchorRef.current = e.currentTarget
      show(openDelay)
    },
    onPointerLeave: (e: ReactPointerEvent) => {
      own.onPointerLeave?.(e)
      if (hover) scheduleHide()
    },
    // Activating the control (click, Enter, Space) is the user moving on.
    onPointerDown: (e: ReactPointerEvent) => {
      own.onPointerDown?.(e)
      hide()
    },
    onFocus: (e: FocusEvent) => {
      own.onFocus?.(e)
      if (!focus || disabled || e.target !== e.currentTarget || !isFocusVisible(e.target)) return
      anchorRef.current = e.currentTarget
      clearTimer()
      openedByRef.current = 'focus'
      setOpen(true)
    },
    onBlur: (e: FocusEvent) => {
      own.onBlur?.(e)
      if (focus) hide()
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      own.onKeyDown?.(e)
      if (e.key === 'Enter' || e.key === ' ') hide()
    },
  }
  if (open && describe) {
    merged['aria-describedby'] = own['aria-describedby'] ? `${own['aria-describedby']} ${id}` : id
  }

  return (
    <>
      {cloneElement(child, merged)}
      {open
        ? createPortal(
            <div
              ref={tipRef}
              id={id}
              role="tooltip"
              style={{
                position: 'fixed',
                top: pos?.top ?? 0,
                left: pos?.left ?? 0,
                visibility: pos ? 'visible' : 'hidden',
              }}
              className="pointer-events-none z-[70] w-max max-w-[240px] rounded-md bg-fg px-2 py-1 text-xs font-medium leading-4 text-bg shadow-md"
            >
              {content}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
